<template>
  <div class="cf-app-layout">
    <div class="cf-app-layout__header">
      <!-- App Header -->
      <cf-app-header :logo-navigation="logoNavigation">
        <template
          v-if="!isLoading"
          #header-left-slot
        >
          <cf-drop-down-menu-button />

          <button
            v-if="isTableTopHomeButton"
            type="button"
            title="Back to table top device landing page"
            class="cf-button cf-button--tertiary"
            @click="tableTopHomeButtonClickHandler"
          >
            <span class="cf-icon cf-icon--small far fa-home-lg-alt" />
          </button>

          <h3
            v-if="isTableNumberDisplayed"
            class="cf-table-number-display"
          >
            {{ userLocationType.description }}

            {{ tableNumberDisplay }}
          </h3>
        </template>

        <!-- Cart button remains in header if non-table device ordering -->
        <template
          v-if="!isLoading"
          #header-right-slot
        >
          <cf-order-at-table-cart-button v-if="isOrderAtTableCartButton" />
        </template>
      </cf-app-header>
    </div>

    <!-- App Content -->
    <div class="cf-app-layout__main-content">
      <div
        v-if="isLoading"
        :class="['cf-container-full-width', { 'cf-page-loader': isLoading }]"
      >
        <div class="cf-loader" />
      </div>

      <template v-else>
        <router-view />
      </template>
    </div>

    <!-- App Footer -->
    <div
      v-if="isFooterHidden"
      class="cf-app-layout__footer"
    >
      <cf-app-footer />
    </div>

    <!-- Cart button moves to bottom and is fixed for table top devices -->
    <div
      v-if="isTableTopOrderCartButton"
      class="cf-app-layout__sticky-bottom-container"
    >
      <cf-order-at-table-cart-button />
    </div>

    <!-- Floating action button for non-table-top order-at-table menu page -->
    <cf-order-at-table-floating-action v-if="isFloatingActionButton" />
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import errorHandlers from '@/services/error-handlers';

  import CfAppHeader from '@/view/components/globals/cf-app-header/index.vue';
  import CfDropDownMenuButton from '@/view/components/globals/cf-app-header/cf-drop-down-menu-button/index.vue';
  import CfOrderAtTableCartButton from '@/view/components/globals/cf-order-at-table-cart-button/index.vue';
  import CfOrderAtTableFloatingAction from '@/view/components/globals/cf-order-at-table-floating-action/index.vue';
  import CfAppFooter from '@/view/components/globals/cf-app-footer/index.vue';


  export default {
    name: 'OrderAtTableLayout',

    metaInfo() {
      const { merchant } = this;

      return {
        titleTemplate(titleChunk) {
          return titleChunk ? `${titleChunk} | Order at Table | ${merchant.name}` : `Order at Table | ${merchant.name}`;
        }
      };
    },

    components: {
      CfAppHeader,
      CfDropDownMenuButton,
      CfOrderAtTableCartButton,
      CfOrderAtTableFloatingAction,
      CfAppFooter
    },

    data() {
      return {
        isLoading: false
      };
    },

    computed: {
      ...mapGetters('locations', ['selectedLocation']),
      ...mapGetters('menu', ['menu']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('orders', ['order', 'tableNumberDisplay', 'userLocationType']),
      ...mapGetters('session', ['currentUser', 'orderDetails', 'session', 'isTableTopDeviceOrdering']),

      logoNavigation() {
        return { isClickable: false };
      },

      isTableNumberDisplayed() {
        return this.tableNumberDisplay && (this.orderDetails.orderType === 'table' || ['dine-in', 'room-service'].includes(this.orderDetails.fulfillmentType));
      },

      isReadOnly() {
        return this.merchant.features['readonly-order-at-table'];
      },

      isOrderAtTableCartButton() {
        const isMenuPage = this.$route.name === 'orderAtTableMenu';

        return !this.isReadOnly && isMenuPage && !this.isTableTopDeviceOrdering;
      },

      isTableTopOrderCartButton() {
        const isMenuPage = this.$route.name === 'orderAtTableMenu';

        const isMenuItemDetailView = this.$route.name === 'orderAtTableMenuItemDetailsPage';

        return this.isTableTopDeviceOrdering && (isMenuPage || isMenuItemDetailView);
      },

      isTableTopHomeButton() {
        const isNotLandingPage = this.$route.name !== 'tableTopWelcomePage';

        return this.isTableTopDeviceOrdering && isNotLandingPage;
      },

      isFooterHidden() {
        return !this.merchant.features['hide-footer-details'];
      },

      isFloatingActionButton() {
        const isMenuPage = this.$route.name === 'orderAtTableMenu';

        return !this.isReadOnly && isMenuPage && !this.isTableTopDeviceOrdering
          && (this.order.orderItemStagedCount > 0 || this.order.orderItemCount > 0);
      },

      isOpenTabEnabled() {
        return this.selectedLocation.features.OpenTab && this.order.orderItemStagedCount === 0 && this.order.orderItemCount === 0;
      }
    },

    created() {
      this.onComponentCreated();
    },

    methods: {
      async onComponentCreated() {
        try {
          this.isLoading = true;

          await this.$store.dispatch('orderAtTable/initializeOrderAtTable');

          if (this.isOpenTabEnabled) {
            this.$openModal('openTabOnboarding');
          }
        }
        catch (error) {
          errorHandlers.silent(error);
        }
        finally {
          this.isLoading = false;
        }
      },

      tableTopHomeButtonClickHandler() {
        const routePayload = { name: 'tableTopWelcomePage' };

        this.$router.push(routePayload);
      }
    }
  };
</script>
